import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@antimatterhq/ui/components/accordion';
import { Icon } from '@antimatterhq/ui/components/icon';
import { useFormikContext } from 'formik';
import { cn } from '@/lib/utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mergeRefs<T = any>(
  ...refs: Array<React.MutableRefObject<T> | React.LegacyRef<T>>
): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null) {
        // eslint-disable-next-line no-param-reassign
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

type FormAccordionContextType = {
  error: string | undefined;
};

const FormAccordionContext = createContext<FormAccordionContextType>({
  error: undefined,
});

const FormAccordion = Accordion;

const FormAccordionProvider = ({
  children,
  error,
}: FormAccordionContextType & { children: React.ReactNode }) => {
  return (
    <FormAccordionContext.Provider value={{ error }}>
      {children}
    </FormAccordionContext.Provider>
  );
};

const FormAccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionItem>,
  React.ComponentPropsWithoutRef<typeof AccordionItem>
>(({ children, ...props }, forwardRef) => {
  const [error, setError] = useState<string | undefined>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const formik = useFormikContext();

  useEffect(() => {
    const errorElement = wrapperRef.current?.querySelector(
      '[aria-label="error"]',
    );
    setError(errorElement?.innerHTML);
  }, [formik.errors, formik.touched]);
  return (
    <AccordionItem ref={mergeRefs(wrapperRef, forwardRef)} {...props}>
      <FormAccordionProvider error={error}>{children}</FormAccordionProvider>
    </AccordionItem>
  );
});
FormAccordionItem.displayName = 'FormAccordionItem';

const FormAccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionTrigger>,
  React.ComponentPropsWithoutRef<typeof AccordionTrigger>
>(({ className, children, ...props }, ref) => {
  const { error } = useContext(FormAccordionContext);
  return (
    <AccordionTrigger ref={ref} {...props}>
      <div className="flex items-center gap-2">
        {children}
        {error && (
          <Icon
            icon="lucide:octagon-alert"
            className="text-red-600 dark:text-red-500"
          />
        )}
      </div>
    </AccordionTrigger>
  );
});
FormAccordionTrigger.displayName = 'FormAccordionTrigger';

const FormAccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionContent>,
  Omit<React.ComponentPropsWithoutRef<typeof AccordionContent>, 'forceMount'>
>(({ className, ...props }, ref) => (
  <AccordionContent
    className={cn('data-[state=closed]:hidden', className)}
    forceMount={true}
    ref={ref}
    {...props}
  />
));
FormAccordionContent.displayName = 'FormAccordionContent';

export {
  FormAccordion,
  FormAccordionItem,
  FormAccordionTrigger,
  FormAccordionContent,
};
